import moment from 'moment'

type SortOrder = 'asc' | 'desc'

/**
 * Sorts an array of objects by a nested key string like 'attributes.created_at'.
 * Supports string, number, and date (ISO string) comparisons.
 */
export function sortByKey<T>(
  array: T[],
  key: string,
  order: SortOrder = 'asc',
): T[] {
  const keys = key.split('.')

  const getValue = (obj: any) => {
    return keys.reduce((acc, k) => (acc ? acc[k] : null), obj)
  }

  return [...array].sort((a, b) => {
    const valA = getValue(a)
    const valB = getValue(b)

    // Handle null or undefined
    if (valA == null && valB == null) return 0
    if (valA == null) return order === 'asc' ? -1 : 1
    if (valB == null) return order === 'asc' ? 1 : -1

    // Dates: detect ISO string format & compare as dates
    if (
      moment(valA, moment.ISO_8601, true).isValid() &&
      moment(valB, moment.ISO_8601, true).isValid()
    ) {
      const dateA = moment(valA)
      const dateB = moment(valB)
      if (dateA.isBefore(dateB)) return order === 'asc' ? -1 : 1
      if (dateA.isAfter(dateB)) return order === 'asc' ? 1 : -1
      return 0
    }

    // Numeric
    if (typeof valA === 'number' && typeof valB === 'number') {
      return order === 'asc' ? valA - valB : valB - valA
    }

    // String
    if (typeof valA === 'string' && typeof valB === 'string') {
      return order === 'asc'
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA)
    }

    return 0
  })
}
